import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { DataRequestKind, PublicPrivacyPage } from '@cloudsign/shared';
import { Alert, Button, SelectField, TextField } from '../../components/ui';
import { ApiClientError } from '../../lib/api';
import { problemText } from '../public-links/api';
import { LinkProblem, PageLoading, PublicShell, Section } from '../public-links/PublicShell';
import { getPrivacy, startRequest, verifyRequest } from './api';
import type { RequestStarted, RequestVerified } from './api';
import { useVisitorLanguage } from './locale';

/** Public privacy page with the data-request flow: /:tenantSlug/privacy (docs/modules/data-protection.md s5). */
export function PrivacyPage() {
  const { tenantSlug = '' } = useParams();
  const q = useQuery({ queryKey: ['public-p4', 'privacy', tenantSlug], queryFn: () => getPrivacy(tenantSlug), retry: false });
  return (
    <PublicShell slug={tenantSlug} hideYourData>
      {q.isPending ? <PageLoading /> : q.isError ? <LinkProblem error={q.error} onRetry={() => void q.refetch()} /> : <PrivacyBody page={q.data} />}
    </PublicShell>
  );
}

function PrivacyBody({ page }: { page: PublicPrivacyPage }) {
  const { vt } = useVisitorLanguage();
  return (
    <>
      <h1 className="text-2xl font-semibold">{vt('privacy.public.title', { name: page.tenantName })}</h1>
      <Section title={vt('privacy.public.notice')}>
        {page.noticeText ? <p className="whitespace-pre-line">{page.noticeText}</p> : null}
        {page.noticeUrl ? (
          <p>
            <a href={page.noticeUrl} target="_blank" rel="noopener noreferrer" className="underline">
              {vt('privacy.public.noticeLink')}
            </a>
          </p>
        ) : null}
      </Section>
      {page.selfServiceEnabled && page.kinds.length > 0 ? (
        <RequestFlow page={page} />
      ) : (
        <Section>
          <p>{vt('privacy.public.selfServiceOff', { name: page.tenantName })}</p>
        </Section>
      )}
    </>
  );
}

function codeProblem(e: unknown, fallback: string): string {
  return e instanceof ApiClientError && (e.status === 400 || e.status === 422) ? fallback : problemText(e);
}

function RequestFlow({ page }: { page: PublicPrivacyPage }) {
  const { vt } = useVisitorLanguage();
  const [kind, setKind] = useState<DataRequestKind>(page.kinds[0] ?? 'access');
  const [v, setV] = useState({ name: '', email: '', phone: '', details: '', website: '' });
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [started, setStarted] = useState<RequestStarted | null>(null);
  const [done, setDone] = useState<RequestVerified | null>(null);
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV((p) => ({ ...p, [k]: e.target.value }));

  const start = useMutation({
    mutationFn: () =>
      startRequest(page.tenantSlug, {
        kind,
        name: v.name.trim(),
        ...(v.email.trim() ? { email: v.email.trim() } : {}),
        ...(v.phone.trim() ? { phone: v.phone.trim() } : {}),
        details: kind === 'rectification' ? v.details.trim() : '',
        website: v.website,
      }),
    onSuccess: (r) => setStarted(r),
  });
  const verify = useMutation({
    mutationFn: (requestId: string) => verifyRequest(page.tenantSlug, { requestId, code }),
    onSuccess: (r) => setDone(r),
  });

  const submitStart = (e: FormEvent) => {
    e.preventDefault();
    const bad: Record<string, string> = {};
    if (v.name.trim() === '') bad['name'] = vt('public.required');
    if (v.email.trim() === '' && v.phone.trim() === '') bad['contact'] = vt('privacy.public.contactNeeded');
    setErrors(bad);
    if (Object.keys(bad).length === 0) start.mutate();
  };

  if (done) {
    const link = statusPath(done.statusUrl);
    return (
      <Section title={vt('privacy.public.done.title')}>
        <Alert kind="success">{vt('privacy.public.done.reference', { reference: done.reference })}</Alert>
        <p>{vt(done.status === 'completed' ? 'privacy.public.done.completed' : 'privacy.public.done.open')}</p>
        {link ? (
          <Link to={link} className="font-medium underline">
            {vt('privacy.public.done.status')}
          </Link>
        ) : null}
      </Section>
    );
  }

  if (started) {
    return (
      <Section title={vt('privacy.public.requestTitle')}>
        <p role="status">{vt(started.channel === 'sms' ? 'privacy.public.sentSms' : 'privacy.public.sentEmail')}</p>
        <form
          noValidate
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            verify.mutate(started.requestId);
          }}
        >
          <TextField label={vt('privacy.public.code')} inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="\d{6}" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />
          {verify.isError ? <Alert>{codeProblem(verify.error, vt('privacy.public.codeWrong'))}</Alert> : null}
          <Button type="submit" variant="primary" className="w-full" disabled={verify.isPending || !/^\d{6}$/.test(code)}>
            {vt('privacy.public.verify')}
          </Button>
        </form>
      </Section>
    );
  }

  return (
    <Section title={vt('privacy.public.requestTitle')}>
      <form onSubmit={submitStart} noValidate className="space-y-3">
        <SelectField label={vt('privacy.public.kind')} value={kind} onChange={(e) => setKind(e.target.value as DataRequestKind)}>
          {page.kinds.map((k) => (
            <option key={k} value={k}>
              {vt(`privacy.public.kind.${k}`)}
            </option>
          ))}
        </SelectField>
        <TextField label={vt('privacy.public.name')} value={v.name} onChange={set('name')} autoComplete="name" required maxLength={120} error={errors['name']} />
        <TextField label={vt('privacy.public.email')} type="email" value={v.email} onChange={set('email')} autoComplete="email" hint={vt('privacy.public.contactHint')} error={errors['contact']} />
        <TextField label={vt('privacy.public.phone')} type="tel" value={v.phone} onChange={set('phone')} autoComplete="tel" />
        {kind === 'rectification' ? <TextField label={vt('privacy.public.details')} value={v.details} onChange={set('details')} maxLength={2000} /> : null}
        {/* Honeypot: hidden from people; the server accepts a filled one silently. */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
          <label>
            {vt('forms.public.honeypot')}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={set('website')} />
          </label>
        </div>
        {start.isError ? <Alert>{problemText(start.error)}</Alert> : null}
        <Button type="submit" variant="primary" className="w-full" disabled={start.isPending}>
          {vt('privacy.public.send')}
        </Button>
      </form>
    </Section>
  );
}

/** The server's status address is a full link; the page only needs its path (/d/:token) to stay on this host. */
function statusPath(url: string): string | null {
  try {
    const u = new URL(url, window.location.origin);
    return /^\/d\/[^/]+$/.test(u.pathname) ? u.pathname : null;
  } catch {
    return null;
  }
}
